import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useNavigate } from '@tanstack/react-router';
import PermissionService from './rolesPermission.service';

type ActionType = 'createRole' | 'updateRole' | 'deleteRole';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useRoleMutation = (): {
  createRole: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  createRoleMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  updateRole: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  updateRoleMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  deleteRole: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  deleteRoleMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  isUpdatingCreateRole: Record<string, boolean>;
  setIsUpdatingCreateRole: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isUpdatingUpdateRole: Record<string, boolean>;
  setIsUpdatingUpdateRole: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isUpdatingDeleteRole: Record<string, boolean>;
  setIsUpdatingDeleteRole: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingCreateRole, setIsUpdatingCreateRole] = useState<Record<string, boolean>>({});
  const [isUpdatingUpdateRole, setIsUpdatingUpdateRole] = useState<Record<string, boolean>>({});
  const [isUpdatingDeleteRole, setIsUpdatingDeleteRole] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const permissionService = PermissionService();

    const action: Record<ActionType, (payload: any) => Promise<any>> = {
      createRole: (params) => permissionService.createRole(params.payload),
      updateRole: (params) => permissionService.updateRole(params.payload),
      deleteRole: (params) => permissionService.deleteRole(params.payload),
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType, ...newRole }) => {
      if (actionType === 'createRole') {
        setIsUpdatingCreateRole({ ...isUpdatingCreateRole, [actionType]: true });
      }
      if (actionType === 'updateRole') {
        setIsUpdatingUpdateRole({ ...isUpdatingUpdateRole, [actionType]: true });
      }
      if (actionType === 'deleteRole') {
        setIsUpdatingDeleteRole({ ...isUpdatingDeleteRole, [actionType]: true });
      }
    },
    onSuccess: (data: any, variables: any) => {
      console.log('Create Customer data:', data);
      navigate({ to: `/permissions` });
      if (variables.actionType === 'createRole') {
        enqueueSnackbar('Role created successfully', { variant: 'success' });
      } else if (variables.actionType === 'updateRole') {
        enqueueSnackbar('Role updated successfully', { variant: 'success' });
      } else if (variables.actionType === 'deleteRole') {
        enqueueSnackbar('Role deleted successfully', { variant: 'success' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during mutation:', error);
      if (variables.actionType === 'updateRole') {
        enqueueSnackbar('Error updating role', { variant: 'error' });
      } else if (variables.actionType === 'deleteRole') {
        enqueueSnackbar('Error deleting role', { variant: 'error' });
      } else if (variables.actionType === 'createRole') {
        enqueueSnackbar('Error creating role', { variant: 'error' });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['role-id'] });
    },
  });

  return {
    createRole: mutation.mutate,
    createRoleMutation: mutation,
    isUpdatingCreateRole,
    setIsUpdatingCreateRole,
    updateRole: mutation.mutate,
    updateRoleMutation: mutation,
    isUpdatingUpdateRole,
    setIsUpdatingUpdateRole,
    deleteRole: mutation.mutate,
    deleteRoleMutation: mutation,
    isUpdatingDeleteRole,
    setIsUpdatingDeleteRole
  };
};

export default useRoleMutation;
